import { useTranslation } from "react-i18next";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { format, getISOWeek } from "date-fns";
import { currentDateFnsLocale } from "../../../i18n/dateLocales";
import { formatPlanPeriodLabel } from "./projectPlanUtils";
import { planGhostBtnClass, planPrimaryBtnClass, planSegmentActiveClass, planSegmentIdleClass } from "./projectPlanTheme";

const PERIOD_OPTIONS = [
  { key: "week", labelKey: "project.plan.toolbar.week" },
  { key: "fortnight", labelKey: "project.plan.toolbar.fortnight" },
  { key: "month", labelKey: "project.plan.toolbar.month" },
];

function CapSummary({ eventsCount, scheduledHours, capacityHours, crewCount, unscheduledCount, t }) {
  const hoursLabel =
    capacityHours > 0
      ? `${Math.round(scheduledHours)}/${Math.round(capacityHours)}`
      : `${Math.round(scheduledHours)}h`;

  return (
    <div className="pp-cap-summary ml-auto hidden shrink-0 lg:flex">
      <div className="pp-cap-cell">
        <span className="lbl">{t("project.plan.stats.events")}</span>
        <span className="val">{eventsCount}</span>
      </div>
      <span className="pp-cap-sep" aria-hidden="true" />
      <div className="pp-cap-cell">
        <span className="lbl">{t("project.plan.stats.scheduled_hours")}</span>
        <span className="val">{hoursLabel}</span>
      </div>
      <span className="pp-cap-sep" aria-hidden="true" />
      <div className="pp-cap-cell">
        <span className="lbl">{t("project.plan.stats.crew")}</span>
        <span className="val text-[#10B981]">{crewCount}</span>
      </div>
      <span className="pp-cap-sep" aria-hidden="true" />
      <div className="pp-cap-cell">
        <span className="lbl">{t("project.plan.stats.unscheduled")}</span>
        <span className={`val ${unscheduledCount > 0 ? "text-[#F59E0B]" : "text-[#10B981]"}`}>
          {unscheduledCount}
        </span>
      </div>
    </div>
  );
}

export function ProjectPlanToolbar({
  periodType,
  onPeriodTypeChange,
  dateFrom,
  dateTo,
  onShiftPeriod,
  onGoToday,
  onNewPlan,
  onScheduleActivity,
  onNewTask,
  onNewAbsence,
  canAddAbsence = false,
  draftCount = 0,
  eventsCount = 0,
  scheduledHours = 0,
  capacityHours = 0,
  crewCount = 0,
  unscheduledCount = 0,
}) {
  const { t } = useTranslation();

  const periodLabel = (() => {
    if (!dateFrom || !dateTo) return "";
    const first = new Date(`${dateFrom}T12:00:00`);
    const last = new Date(`${dateTo}T12:00:00`);
    if (periodType === "month") {
      return format(first, "LLLL yyyy", { locale: currentDateFnsLocale() });
    }
    const rangeLabel = formatPlanPeriodLabel(first, last, currentDateFnsLocale());
    if (periodType === "week") {
      return t("project.plan.toolbar.week_range", {
        week: getISOWeek(first),
        range: rangeLabel,
      });
    }
    if (periodType === "fortnight") {
      const weekFrom = getISOWeek(first);
      const weekTo = getISOWeek(last);
      return t("project.plan.toolbar.fortnight_range", {
        weekFrom,
        weekTo,
        range: rangeLabel,
      });
    }
    return rangeLabel;
  })();

  return (
    <>
      <header className="flex h-[60px] shrink-0 items-center gap-5 border-b border-[#E5E9F0] bg-white px-5">
        <div className="brand flex items-center gap-2.5">
          <span className="brand-mark flex h-[26px] w-[26px] items-center justify-center rounded-[7px] bg-gradient-to-br from-[#2B6CFF] to-[#6B8DFF] text-xs font-bold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]">
            P
          </span>
          <span className="text-[15px] font-semibold tracking-tight text-[#0B1220]">
            {t("project.plan.title")}
          </span>
        </div>

        <nav className="crumbs hidden items-center gap-2 text-[13px] text-[#6B7682] md:flex">
          <span>{t("project.plan.toolbar.breadcrumb")}</span>
        </nav>

        <div className="flex-1" />

        <div className="top-actions flex flex-wrap items-center gap-2">
          <button type="button" onClick={onNewPlan} className={planGhostBtnClass}>
            {t("project.plan.create_plan")}
            {draftCount > 0 ? (
              <span className="rounded-full bg-[#EEF2F7] px-2 py-0.5 text-xs font-semibold">
                {draftCount}
              </span>
            ) : null}
          </button>
          <button type="button" onClick={onScheduleActivity} className={planGhostBtnClass}>
            <ClipboardDocumentListIcon className="h-4 w-4 text-violet-600" />
            {t("project.plan.toolbar.schedule_activity")}
          </button>
          {canAddAbsence ? (
            <button type="button" onClick={onNewAbsence} className={planGhostBtnClass}>
              <PlusIcon className="h-4 w-4 text-amber-600" />
              {t("project.plan.toolbar.new_absence")}
            </button>
          ) : null}
          <button type="button" onClick={onNewTask} className={planPrimaryBtnClass}>
            <PlusIcon className="h-4 w-4" />
            {t("project.plan.toolbar.new_task")}
          </button>
        </div>
      </header>

      <div className="cal-toolbar flex flex-wrap items-center gap-3 border-b border-[#E5E9F0] bg-[#F6F8FB] px-5 py-3.5">
        <div className="pp-view-switcher">
          {PERIOD_OPTIONS.map(({ key, labelKey }) => (
            <button
              key={key}
              type="button"
              onClick={() => onPeriodTypeChange?.(key)}
              className={periodType === key ? planSegmentActiveClass : planSegmentIdleClass}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        <div className="month-nav flex items-center gap-1">
          <button
            type="button"
            onClick={() => onShiftPeriod?.(-1)}
            className="pp-btn pp-btn-icon flex h-8 w-8 items-center justify-center p-0"
            aria-label={t("project.plan.toolbar.prev")}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="month-label min-w-[160px] text-center text-base font-semibold tracking-tight text-[#0B1220]">
            {periodLabel}
          </span>
          <button
            type="button"
            onClick={() => onShiftPeriod?.(1)}
            className="pp-btn pp-btn-icon flex h-8 w-8 items-center justify-center p-0"
            aria-label={t("project.plan.toolbar.next")}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>

        <button type="button" onClick={() => onGoToday?.()} className={planGhostBtnClass}>
          {t("project.plan.toolbar.today")}
        </button>

        <CapSummary
          eventsCount={eventsCount}
          scheduledHours={scheduledHours}
          capacityHours={capacityHours}
          crewCount={crewCount}
          unscheduledCount={unscheduledCount}
          t={t}
        />
      </div>
    </>
  );
}
